export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let cleanHex = hex.replace('#', '').trim();
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(cleanHex, 16);
  if (isNaN(num)) {
    return { r: 245, g: 158, b: 11 }; // Default Yellow / Gold (#f59e0b)
  }
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

export function adjustBrightness(hex: string, percent: number): string {
  const { r, g, b } = hexToRgb(hex);
  const adjust = (val: number) => {
    const res = Math.round(val + (255 - val) * (percent / 100));
    return Math.min(255, Math.max(0, res));
  };
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}

export function resetSystemTheme(): void {
  const root = document.documentElement;
  // Remove as sobreposições de cores para restaurar as cores padrão nativas do Tailwind v4
  root.style.removeProperty('--color-amber-300');
  root.style.removeProperty('--color-amber-400');
  root.style.removeProperty('--color-amber-500');
  root.style.removeProperty('--color-amber-600');
  root.style.removeProperty('--color-yellow-300');
  root.style.removeProperty('--color-yellow-400');
  root.style.removeProperty('--color-yellow-500');

  root.style.setProperty('--theme-accent', '#f59e0b');
  root.style.setProperty('--theme-accent-rgb', '245, 158, 11');
  root.style.setProperty('--theme-accent-glow', 'rgba(245, 158, 11, 0.4)');
  root.style.setProperty('--theme-accent-subtle', 'rgba(245, 158, 11, 0.15)');
  root.style.setProperty('--theme-accent-border', 'rgba(245, 158, 11, 0.4)');
  root.style.setProperty('--theme-accent-text', '#fde047');
  root.style.setProperty('--theme-accent-hover', '#d97706');
  root.removeAttribute('data-profile-color');

  let metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', '#f59e0b');
  }
}

export function applyProfileTheme(colorHex?: string): void {
  // Se estiver no Painel Gerenciador, mantém isolamento total das cores do sistema
  if (typeof document !== 'undefined' && (
    document.body.classList.contains('is-gerenciador') ||
    document.documentElement.classList.contains('is-gerenciador') ||
    document.documentElement.classList.contains('gerenciador-isolated-scope') ||
    (typeof window !== 'undefined' && window.location.pathname.includes('gerenciador'))
  )) {
    resetSystemTheme();
    return;
  }

  const hex = colorHex && colorHex.startsWith('#') ? colorHex : '#f59e0b';
  const { r, g, b } = hexToRgb(hex);

  const root = document.documentElement;
  root.style.setProperty('--theme-accent', hex);
  root.style.setProperty('--theme-accent-rgb', `${r}, ${g}, ${b}`);
  root.style.setProperty('--theme-accent-glow', `rgba(${r}, ${g}, ${b}, 0.35)`);
  root.style.setProperty('--theme-accent-subtle', `rgba(${r}, ${g}, ${b}, 0.15)`);
  root.style.setProperty('--theme-accent-border', `rgba(${r}, ${g}, ${b}, 0.4)`);
  root.style.setProperty('--theme-accent-text', adjustBrightness(hex, 30));
  root.style.setProperty('--theme-accent-hover', adjustBrightness(hex, -15));

  // Sincroniza dinamicamente as variáveis de cor Tailwind v4 para que tons de amarelo/âmbar sigam o perfil no catálogo/player
  root.style.setProperty('--color-amber-300', adjustBrightness(hex, 40));
  root.style.setProperty('--color-amber-400', adjustBrightness(hex, 20));
  root.style.setProperty('--color-amber-500', hex);
  root.style.setProperty('--color-amber-600', adjustBrightness(hex, -15));
  root.style.setProperty('--color-yellow-300', adjustBrightness(hex, 40));
  root.style.setProperty('--color-yellow-400', adjustBrightness(hex, 20));
  root.style.setProperty('--color-yellow-500', hex);

  root.setAttribute('data-profile-color', hex);

  // Update mobile/browser theme-color meta if available
  let metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', hex);
  }
}

